'use client';

import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';

import { cn } from '@/lib/utils';

/** O raio do círculo no sistema de coordenadas do SVG; a caixa é 2r por 2r. */
const RAIO = 50;

const GRAUS_NA_VOLTA = 360;
const MEIA_VOLTA = 180;

/** Uma fatia desenhada: o tamanho dela e a classe de preenchimento do tema. */
export interface IFatiaDesenhada {
  chave: string;
  valor: number;
  /** Classe `fill-*` do tema. A pizza nunca escolhe um tom próprio. */
  preenche: string;
  /** O que a fatia responde ao passar o mouse. */
  rotulo: string;
  onClick?: () => void;
}

/** O ponto da borda do círculo num ângulo, com 0 grau no topo e girando à direita. */
function pontoDaBorda(graus: number): { x: number; y: number } {
  const radianos = ((graus - 90) * Math.PI) / MEIA_VOLTA;
  return { x: RAIO + RAIO * Math.cos(radianos), y: RAIO + RAIO * Math.sin(radianos) };
}

/** O caminho de uma fatia: do centro para a borda, o arco, e de volta ao centro. */
function caminhoDaFatia(de: number, ate: number): string {
  const inicio = pontoDaBorda(de);
  const fim = pontoDaBorda(ate);
  const arcoGrande = ate - de > MEIA_VOLTA ? 1 : 0;
  return `M ${RAIO} ${RAIO} L ${inicio.x} ${inicio.y} A ${RAIO} ${RAIO} 0 ${arcoGrande} 1 ${fim.x} ${fim.y} Z`;
}

interface ProducaoPizzaProps {
  fatias: Array<IFatiaDesenhada>;
  /** A leitura da pizza inteira, para quem usa leitor de tela. */
  rotulo: string;
  /** What the slot says when the chosen measure adds up to nothing to draw. */
  vazioTexto: string;
  className?: string;
}

/**
 * A pizza das marcas, em SVG puro: o projeto não tem biblioteca de gráfico e um gráfico
 * de uma tela não justifica instalar uma.
 *
 * Uma marca sozinha vira um círculo inteiro em vez de um arco: um arco de 360 graus tem
 * começo e fim no mesmo ponto e o navegador não desenha nada.
 */
export function ProducaoPizza({ fatias, rotulo, vazioTexto, className }: ProducaoPizzaProps) {
  const total = fatias.reduce((soma, fatia) => soma + fatia.valor, 0);
  const visiveis = fatias.filter((fatia) => fatia.valor > 0);

  if (total <= 0 || visiveis.length === 0) {
    return <NoDataFound variant="empty" compact icon="pie_chart" title={vazioTexto} />;
  }

  let anguloAtual = 0;

  return (
    <svg viewBox={`0 0 ${RAIO * 2} ${RAIO * 2}`} className={cn('size-full', className)} role="img" aria-label={rotulo}>
      {visiveis.length === 1 ? (
        <circle
          cx={RAIO}
          cy={RAIO}
          r={RAIO}
          className={cn(visiveis[0].preenche, visiveis[0].onClick !== undefined && 'cursor-pointer')}
          onClick={visiveis[0].onClick}
        >
          <title>{visiveis[0].rotulo}</title>
        </circle>
      ) : (
        visiveis.map((fatia) => {
          const de = anguloAtual;
          anguloAtual += (fatia.valor / total) * GRAUS_NA_VOLTA;
          return (
            <path
              key={fatia.chave}
              d={caminhoDaFatia(de, anguloAtual)}
              className={cn(
                'stroke-surface transition-opacity hover:opacity-80',
                fatia.preenche,
                fatia.onClick !== undefined && 'cursor-pointer',
              )}
              strokeWidth={1}
              onClick={fatia.onClick}
            >
              <title>{fatia.rotulo}</title>
            </path>
          );
        })
      )}
    </svg>
  );
}
